import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import {
  type PaymentMethod as TPaymentMethod
} from "../../../payments/methods/slice";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface PaymentMethod extends Pick<TPaymentMethod, 'title' | 'image'> { 
  id: number;
}

export interface Payment {
  id: number;
  title: string;
  geo: string;
  isActive: number;
}

export interface PaymentExtended extends Payment {
  payments: PaymentMethod[];
}

export interface PaymentDraftItem extends PaymentExtended {
  isModified: boolean;
}

export interface PaymentsState {
  list: Payment[];
  isListFetched: boolean;
  isItemFetched: boolean;
  isItemFetching: boolean;
  selectedItems: Payment['id'][];
  processingItemId?: Payment['id'];
  activePage: number;
  totalItems: number;
  totalPages: number;
  draftItem: PaymentDraftItem;
};

interface getPaymentsListResponse {
  items: Payment[];
  totalItems: number;
  totalPages: number;
};

interface getPaymentsPayload {
  limit?: number;
  offset?: number;
  title?: string;
  countries?: string[];
  isActive?: number;
  order?: Partial<Record<keyof Payment, string>>
};

type updatePaymentPayload
  = Omit<PaymentDraftItem, 'isModified' | 'geo' | 'payments'> & {
      paymentIds: number[];
      country: string;
  };
    
type createPaymentPayload
  = Omit<updatePaymentPayload, 'id'>

const createDraftItem = (data?: PaymentExtended): PaymentDraftItem => {

  if (!data) {
    return {
      id: 0,
      title: '',
      geo: '',
      isActive: 0,
      payments: [],
      isModified: false,
    };
  }

  return {
    id: data.id,
    title: data.title,
    geo: data.geo,
    isActive: data.isActive,
    payments: data.payments,
    isModified: false,
  };
};

const initialState: PaymentsState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  isItemFetching: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 1,
  selectedItems: [],
  draftItem: createDraftItem(),
};

const bunchUpdatePayments = createAsyncThunk(
  "cms-payments/bunch-update",
  async (data: { ids: number[]; isActive: number }) => {
    const result = await instance.put(apiRoutes.paymentsBunchUpdate, data);

    return result.data;
  }
);

const getPaymentsList = createAsyncThunk(
  "cms-payments/list",
  async (data: getPaymentsPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.paymentsList}`, data);
      return result.data as getPaymentsListResponse;
    } catch (error) {
      throw error;
    }
  }
);

const deletePaymentsItem = createAsyncThunk(
  "cms-payments/delete",
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.paymentsDelete}/${id}`);
      return true;
    } catch (error) {
      throw error;
    }
  }
);

const getPaymentItem = createAsyncThunk(
  "cms-payments/item",
  async (id: number) => {
    try {
      const result = await instance.get(`${apiRoutes.paymentsItem}/${id}`);
      return result.data as PaymentExtended;
    } catch (error) {
      throw error;
    }
  }
);

const createPaymentItem = createAsyncThunk<
  PaymentExtended,
  createPaymentPayload,
  { rejectValue: ApiErrorPayload }
>(
  "cms-payments/create",
  async (data: createPaymentPayload, { rejectWithValue }) => {
    try {
      const result = await instance.post(`${apiRoutes.paymentsCreate}`, data);
      return result.data as PaymentExtended;
    } catch (error: any) {
      const apiError = error?.data?.error;
      if (apiError?.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const updatePaymentItem = createAsyncThunk<
  PaymentExtended,
  updatePaymentPayload,
  { rejectValue: ApiErrorPayload }
>(
  "cms-payments/update",
  async (data: updatePaymentPayload, { rejectWithValue }) => {
    try {
      const result = await instance.put(
        `${apiRoutes.paymentsUpdate}/${data.id}`,
        data,
      );
      return result.data as PaymentExtended;
    } catch (error: any) {
      const data = error?.data;
      const apiError = data?.error ?? data;

      if (apiError && apiError.code) {
        return rejectWithValue(apiError);
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

const paymentsSlice = createSlice({
  name: "cms-payments",
  initialState,
  reducers: {
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        )
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleSelectAll: (state) => {
      state.selectedItems = state.selectedItems.length !== state.list.length
        ? state.list.map((item) => item.id)
        : [];
    },
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleClearDraftItem: (state) => {
      state.draftItem = createDraftItem();
      state.isItemFetched = false;
    },
    handleUpdateDraftItem: (state, action: PayloadAction<Partial<PaymentDraftItem>>) => {
      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        isModified: true
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPaymentsList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(getPaymentItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.draftItem = createDraftItem(action.payload);
    });
    builder.addCase(deletePaymentsItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deletePaymentsItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deletePaymentsItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updatePaymentItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updatePaymentItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.payload.id
          ? mergeExistingFields(item, action.payload)
          : item,
      );
      state.processingItemId = undefined;
      state.draftItem = {
        ...state.draftItem,
        ...action.payload,
        payments: state.draftItem.payments, 
        isModified: false,
      };
    });
    builder.addCase(updatePaymentItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
    builder.addCase(createPaymentItem.pending, (state) => {
      state.isItemFetching = true;
    })
    builder.addCase(createPaymentItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createPaymentItem.rejected, (state) => {
      state.isItemFetching = false;
    })
    builder.addCase(bunchUpdatePayments.fulfilled, (state) => {
      state.isListFetched = false;
      state.selectedItems = [];
    });
  }
});

const {
  handleSelect,
  handleSelectAll,
  handlePaginate,
  handleClearDraftItem,
  handleUpdateDraftItem
} = paymentsSlice.actions;

export {
  paymentsSlice,
  handleSelect,
  handleSelectAll,
  handlePaginate,
  getPaymentsList,
  deletePaymentsItem,
  getPaymentItem,
  updatePaymentItem,
  handleClearDraftItem,
  handleUpdateDraftItem,
  createPaymentItem,
  bunchUpdatePayments
};
